import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { ReactivateDeclinedRejectedModal } from '@components/modals/ReactivateQuoteModals/ReactivateDeclinedRejectedModal/ReactivateDeclinedRejectedModal';
import { ReactivateExpiredCancelledModal } from '@components/modals/ReactivateQuoteModals/ReactivateExpiredCancelledModal';
import { ToastType } from '@components/toast';
import { PolicyTransactionStatus } from '@lio/rule-engine';

import { QuoteStatus, QuoteType, TQuoteApiResponse } from '@models/quotes';
import { Typography } from '@mui/material';
import { Grid } from '@mui/system';

import { useGetPolicy, useSearchPolicyTransactions } from '@services/policy';
import { useReactivateCanceledQuote } from '@services/quotes/useReactivateCanceledQuote';
import { useSearchQuoteCancelation } from '@services/quotes/useSearchQuoteCancelation';
import { useUpdateQuote } from '@services/quotes/useUpdateQuote';
import { useStore } from '@store/GlobalStore';

import { setDateToMidnight } from '@utils/date';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';

import { shallow } from 'zustand/shallow';

export type ReactivateQuoteDialogWrapperProps = {
  quote: TQuoteApiResponse;
  hideActionDialog: () => void;
  onStatusRead: () => void;
  showReactivateDialog: boolean;
  declinedRejectedQuoteDetails: {
    declinedRejectedQuotePolicyNumber: string;
    declinedRejectedQuoteId: string;
    declinedRejectedQuoteStatus: string;
  };
  handleCloseReactivateDeclinedRejectedQuoteDialog: () => void;
  policyNumber?: string;
};

export const ReactivateQuoteDialogWrapper = ({
  quote,
  hideActionDialog,
  onStatusRead,
  declinedRejectedQuoteDetails,
  showReactivateDialog,
  handleCloseReactivateDeclinedRejectedQuoteDialog,
  policyNumber,
}: ReactivateQuoteDialogWrapperProps) => {
  const { data: policy } = useGetPolicy({ policyId: quote?.policyId });

  const [showConfirmOutOfDateDialog, setShowConfirmOutOfDateDialog] = useState<boolean>(false);

  const { data: policyTransactionsData, isLoading } = useSearchPolicyTransactions(
    { policyId: policy?.id ?? '', status: [PolicyTransactionStatus.Processed] },
    [QuoteType.Renewal, QuoteType.Endorsement].includes(quote?.quoteType as QuoteType),
  );

  const mustConfirmOutOfDate = useMemo(() => {
    const hasProcessedTransactionsAfterQuoteCreation = policyTransactionsData?.items?.some((transaction) => {
      return new Date(transaction.createdAt) > new Date(quote?.createdAt);
    });

    return !!hasProcessedTransactionsAfterQuoteCreation;
  }, [policyTransactionsData, quote?.createdAt]);

  useEffect(() => {
    if (mustConfirmOutOfDate) {
      setShowConfirmOutOfDateDialog(true);
    }
  }, [showReactivateDialog, declinedRejectedQuoteDetails, mustConfirmOutOfDate]);

  const [currentUser] = useStore((state) => [state.currentUser], shallow);

  const { reactivateQuote, isPending: isReactivateCanceledQuoteLoading } = useReactivateCanceledQuote();
  const { updateQuote, isPending: isUpdateQuoteLoading } = useUpdateQuote();

  const { quoteCancelation } = useSearchQuoteCancelation({
    quoteId: quote?.id,
  });

  const quoteId = quote?.id;

  const reactivateExpiredQuote = (effectiveDate: Date | null) => {
    if (!effectiveDate) {
      console.error('EffectiveDate not found');
      hideActionDialog();
      return;
    }
    updateQuote({
      quoteId,
      quoteEffective: setDateToMidnight(effectiveDate).toISOString(),
    }).finally(() => {
      hideActionDialog();
    });
  };

  const reactivateCanceledQuote = async (effectiveDate: Date | null) => {
    if (quote && quoteCancelation?.items && quoteCancelation?.items.length > 0) {
      try {
        await reactivateQuote(
          {
            quoteId: quote?.id,
            quoteCancelationId: quoteCancelation?.items[0]?.id,
            reinstatedByUserId: currentUser?.id,
            quoteEffective: effectiveDate ? setDateToMidnight(effectiveDate).toISOString() : undefined,
          },
          {
            onSuccess: () => {
              onStatusRead();
              enqueueSnackbar(`Quote #${quote?.policyNumber?.policyNumber} was successfully reactivated.`, {
                preventDuplicate: true,
                variant: ToastType.Success,
              });
            },
            onError: () => {
              onStatusRead();
              enqueueSnackbar(`Reactivation of quote #${quote?.policyNumber?.policyNumber} has failed.`, {
                preventDuplicate: true,
                variant: ToastType.Error,
                persist: true,
              });
            },
          },
        );
      } finally {
        hideActionDialog();
      }
    }
  };

  if (isLoading) {
    return null;
  }

  return showConfirmOutOfDateDialog ? (
    <ConfirmationDialog
      isOpen={showReactivateDialog || !!declinedRejectedQuoteDetails.declinedRejectedQuoteId}
      onCancel={() => {
        setShowConfirmOutOfDateDialog(false);
        if (showReactivateDialog) {
          hideActionDialog();
        } else {
          handleCloseReactivateDeclinedRejectedQuoteDialog();
        }
      }}
      dialogPadding='0px 0px 20px 0px'
      title={`Warning: This ${quote?.quoteType} is out of date`}
      buttonConfig={{
        confirmText: 'Yes, Continue',
        onConfirm: () => {
          setShowConfirmOutOfDateDialog(false);
        },
        secondaryText: 'No, Exit',
        alignment: 'right',
      }}
    >
      <Grid container spacing={2} sx={{ paddingX: '25px' }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant='bodySmall' display='block' mb={2}>
            This policy has a transaction that was bound after this {quote?.quoteType?.toLowerCase()} was created, so it
            will potentially not have all current policy details on it. Creating a new {quote?.quoteType?.toLowerCase()}{' '}
            might be a better option.
          </Typography>
          <Typography variant='bodySmall' display='block'>
            Are you sure you want to reactivate this {quote?.quoteType?.toLowerCase()} transaction?
          </Typography>
        </Grid>
      </Grid>
    </ConfirmationDialog>
  ) : (
    <>
      <ReactivateExpiredCancelledModal
        effectiveOnDate={quote.quoteType === QuoteType.Renewal ? quote.effectiveOn : undefined}
        isOpen={showReactivateDialog}
        isLoading={isUpdateQuoteLoading || isReactivateCanceledQuoteLoading}
        policyNumber={quote?.policyNumber?.policyNumber}
        onCancel={() => hideActionDialog()}
        quoteStatus={quote?.status}
        onConfirm={quote?.status.name === QuoteStatus.Expired.name ? reactivateExpiredQuote : reactivateCanceledQuote}
      />

      <ReactivateDeclinedRejectedModal
        isOpen={!!declinedRejectedQuoteDetails.declinedRejectedQuoteId}
        quoteId={declinedRejectedQuoteDetails.declinedRejectedQuoteId}
        policyNumber={policyNumber ?? declinedRejectedQuoteDetails.declinedRejectedQuotePolicyNumber}
        currentStatusId={declinedRejectedQuoteDetails.declinedRejectedQuoteStatus}
        onCloseModal={handleCloseReactivateDeclinedRejectedQuoteDialog}
      />
    </>
  );
};
